import { Fragment } from 'react';
import { MARK_TAG, opensInNewTab, parseInline, type InlineSegment } from './parseInline';

const LINK_CLASS = {
  body: 'text-accent hover:text-accent-hover underline',
  caption: 'underline decoration-neutral-300 underline-offset-2 hover:text-neutral-700',
  inverse: 'underline decoration-white/30 underline-offset-2 hover:text-white',
} as const;

/**
 * Where the text sits. `body` is article prose, `caption` the small grey line
 * under a photograph, `inverse` that same line over a photograph or a dark
 * backdrop.
 */
export type InlineTone = keyof typeof LINK_CLASS;

const CODE_CLASS = 'bg-neutral-100 px-1.5 py-0.5 rounded text-sm';

interface InlineMarkdownProps {
  /** The stored field, in the markdown `parseInline.ts` describes. */
  text: string;
  tone?: InlineTone;
  /**
   * Whether a link in the text becomes an anchor. False where the text is
   * already inside one (a booking button, a rating label with its own URL):
   * an anchor inside an anchor is invalid HTML, and the browser's repair of it
   * is not something to render an article through. The label still shows.
   */
  links?: boolean;
}

function renderSegments(
  segments: InlineSegment[],
  tone: InlineTone,
  links: boolean,
): React.ReactNode {
  return segments.map((segment, i) => {
    if (segment.type === 'text') return <Fragment key={i}>{segment.value}</Fragment>;

    const children = renderSegments(segment.content, tone, links);

    if (segment.type === 'mark') {
      const Tag = MARK_TAG[segment.mark];
      return (
        <Tag key={i} className={segment.mark === 'code' ? CODE_CLASS : undefined}>
          {children}
        </Tag>
      );
    }

    if (!links) return <Fragment key={i}>{children}</Fragment>;

    const external = opensInNewTab(segment.href);
    return (
      <a
        key={i}
        href={segment.href}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        className={LINK_CLASS[tone]}
      >
        {children}
      </a>
    );
  });
}

/**
 * A block field's text with its inline markdown rendered.
 *
 * Renders inline only: the caller owns the element around it and its type
 * styling, because a checklist item, a table cell and a photo credit each want
 * a different size and alignment.
 */
export function InlineMarkdown({ text, tone = 'body', links = true }: InlineMarkdownProps) {
  return <>{renderSegments(parseInline(text), tone, links)}</>;
}
